import { convertCurrencyFromApi } from '../../common/helpers/currency';
import { OrdersApiService } from '../../common/services/orders.api.service';
import { OrderDiscountsApiService } from '../../common/services/order-discounts.api.service';
import { OrderPaymentMethodsApiService } from '../../common/services/order-payment-methods.api.service';
import { OrderShippingMethodsApiService } from '../../common/services/order-shipping-methods.api.service';
import { OrderItemModel } from '../../common/models/order-item.model';
import OrderItemsTransformer from '../../common/helpers/orderItemsTransformer';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import ORDER_STATE from '../../common/enums/orderState';

const DEFAULT_TAX_GROUP_ID = 1;

function _generateEmptyItem(name, order) {
  const orderPaymentPrice = { [order.currencyId]: convertCurrencyFromApi(order.payment.price)[order.currencyId] };
  const orderPaymentTaxedPrice = {
    [order.currencyId]: convertCurrencyFromApi(order.payment.taxed_price)[order.currencyId],
  };

  const emptyItem = {
    [OrderItemModel.ATTR_ID]: null,
    [OrderItemModel.ATTR_PRODUCT_ID]: null,
    [OrderItemModel.ATTR_PRODUCT_VARIATION_ID]: null,
    [OrderItemModel.ATTR_AMOUNT]: 1,
    [OrderItemModel.ATTR_POSITION]: 1,
    [OrderItemModel.ATTR_NAME]: name,
    [OrderItemModel.ATTR_UNIT_PRICE]: orderPaymentPrice,
    [OrderItemModel.ATTR_TAXED_UNIT_PRICE]: orderPaymentTaxedPrice,
    [OrderItemModel.ATTR_PRICE]: orderPaymentPrice,
    [OrderItemModel.ATTR_TAXED_PRICE]: orderPaymentTaxedPrice,
    [OrderItemModel.ATTR_TOTAL_PRICE]: orderPaymentPrice,
    [OrderItemModel.ATTR_TAXED_TOTAL_PRICE]: orderPaymentTaxedPrice,
    [OrderItemModel.ATTR_TAX_GROUP_ID]: DEFAULT_TAX_GROUP_ID,
  };

  return emptyItem;
}

export default {
  namespaced: true,

  state: {
    itemsToRemove: [],
    status: REQUEST_STATUS.SUCCESS,
  },

  getters: {
    getItemsToRemove: state => state.itemsToRemove,
    getStatus: state => state.status,
  },

  mutations: {
    SET_ITEMS(state, payload) {
      state.itemsToRemove = payload;
    },
    FETCH_REQUEST(state) {
      state.status = REQUEST_STATUS.LOADING;
    },
    FETCH_SUCCESS(state) {
      state.status = REQUEST_STATUS.SUCCESS;
    },
    FETCH_FAIL(state) {
      state.status = REQUEST_STATUS.FAIL;
    },
  },

  actions: {
    updateItemsToRemove({ commit }, itemsToRemove) {
      commit('SET_ITEMS', itemsToRemove);
    },

    postOrderItems({ commit, dispatch, getters, rootGetters }, orderId) {
      commit('FETCH_REQUEST');

      const order = rootGetters['orders/getOrder'](orderId);

      if (!order) {
        return;
      }

      const itemsToRemoveIds = getters.getItemsToRemove.map(itemToRemove => itemToRemove.id);
      const orderItems = OrderItemsTransformer.transformForOrderItemsUpdate(order, itemsToRemoveIds);

      return OrdersApiService.patch(orderId, {
        items: orderItems,
        order_state_id: ORDER_STATE.CARRIER_PAYMENT_COMPLETED,
        notify_change: true,
        prefer_taxed_prices: true,
      })
        .then(response => {
          dispatch('handleSuccess', this.$app.$vuetify.lang.t('$vuetify.messages.orderSuccessfullyUpdated'));
          return Promise.resolve(response.getItem());
        })
        .catch(response => {
          dispatch('handleError', response);
          return Promise.reject(response);
        })
        .finally(() => {
          commit('SET_ITEMS', []);
        });
    },

    cancelOrder({ commit, dispatch, rootGetters }, orderId) {
      commit('FETCH_REQUEST');

      const order = rootGetters['orders/getOrder'](orderId);
      const languageIds = rootGetters['languages/getIds'];
      const name = languageIds.reduce((acc, languageId) => {
        acc[languageId] = this.$app.$vuetify.lang.t('$vuetify.messages.carrier');
        return acc;
      }, {});

      if (!order) {
        return;
      }

      const orderItems = [];

      const orderPaymentData = {
        payment_method_id: order.payment.payment_method_id,
        price: { [order.currencyId]: 0 },
        taxed_price: {
          [order.currencyId]: 0,
        },
      };

      const orderShippingData = {
        shipping_method_id: order.shipping.shipping_method_id,
        price: { [order.currencyId]: 0 },
        taxed_price: {
          [order.currencyId]: 0,
        },
      };

      return OrdersApiService.patch(orderId, {
        items: orderItems,
        order_state_id: ORDER_STATE.CANCELLED,
        prefer_taxed_prices: true,
      })
        .then(() => {
          const payment = OrderPaymentMethodsApiService.patch(orderId, order.payment.id, orderPaymentData);
          const shipping = OrderShippingMethodsApiService.patch(orderId, order.shipping.id, orderShippingData);

          let promises = [payment, shipping];

          Promise.all(promises).finally(async () => {
            for (let discount of order.discounts) {
              await OrderDiscountsApiService.delete(orderId, discount.id);
            }

            Promise.resolve();
          });
        })
        .then(() => {
          dispatch('handleSuccess', this.$app.$vuetify.lang.t('$vuetify.messages.orderSuccessfullyCancelled'));
          return Promise.resolve();
        })
        .catch(response => {
          dispatch('handleError', response);
          return Promise.reject(response);
        })
        .finally(() => {
          commit('SET_ITEMS', []);
        });
    },

    handleError({ commit }, response) {
      commit('FETCH_FAIL');
      const message = response.getFirstValidationErrorMessage() || response.getMessage();
      this.$app.$alertError(message);
    },

    handleSuccess({ commit }, message) {
      commit('FETCH_SUCCESS');
      this.$app.$alertSuccess(message);
    },
  },
};
